<template>
  <div class="flex flex-col space-y-4 lg:space-y-6">
    <div class="space-y-4">
      <Card>
        <div class="grid sm:grid-cols-2 sm:items-center gap-4">
          <div class="sm:text-lg">
            {{ $t("site.settings.language.labelChapterLanguages") }}
          </div>
          <div>
            <SelectMultipleAsync
              :modelValue="translationLocales ?? []"
              @update:modelValue="
                (newLocales) =>
                  updateTranslationLocales(newLocales as TranslationLocales)
              "
              :fetcher="getAllTranslationLocales"
              :placeholder="
                translate('site.settings.language.selectPlaceholder')
              "
            />
          </div>
        </div>
      </Card>
      <Card v-if="authStore?.user">
        <div class="grid sm:grid-cols-2 sm:items-center gap-4">
          <h2 class="text-base sm:text-lg my-auto">
            {{
              userHasVerifiedAge
                ? translate("site.settings.site.labelMatureContent")
                : translate("site.settings.site.labelVerifyAge")
            }}
          </h2>
          <div
            v-if="userHasVerifiedAge"
            class="flex justify-center sm:justify-end"
          >
            <div class="my-2 flex items-center space-x-2 p-2">
              <NamiSwitch
                :modelValue="!!matureEnabled"
                @update:modelValue="updateMature"
              />
              <span
                class="text-sm sm:text-base font-medium"
                :class="{ 'text-nami-comi-blue': matureEnabled }"
              >
                {{
                  matureEnabled
                    ? translate("site.settings.site.toggleEnabled")
                    : translate("site.settings.site.toggleDisabled")
                }}
              </span>
            </div>
          </div>
          <div v-else class="flex justify-center sm:justify-end">
            <div class="p-2">
              <NamiButton
                buttonType="primary"
                @buttonClick="showConfirmAgeModal = true"
              >
                {{ $t("site.settings.site.buttonVerify") }}
              </NamiButton>
            </div>
          </div>
        </div>
      </Card>
      <Card v-else>
        {{ $t("site.settings.loginRequired") }}
      </Card>
      <div class="flex justify-end">
        <NamiButton
          class="sm:min-w-[15rem]"
          @buttonClick="emit('update')"
          buttonType="primary"
          :disabled="isUpdating"
        >
          {{ $t("site.settings.buttonSave") }}
        </NamiButton>
      </div>
    </div>
  </div>
  <NamiModalPresets
    v-model="showConfirmAgeModal"
    :options="{
      title: translate('site.settings.site.modalConfirmAgeTitle'),
      detail: translate('site.settings.site.modalConfirmAgeDetail'),
      icon: 'okay',
      buttons: [
        {
          buttonType: 'secondary',
          buttonText: translate(
            'site.settings.site.modalConfirmAgeButtonCancel',
          ),
          disabled: isPendingAction,
          onButtonClick: () => (showConfirmAgeModal = false),
        },
        {
          buttonType: 'primary',
          buttonText: translate(
            'site.settings.site.modalConfirmAgeButtonConfirm',
          ),
          disabled: isPendingAction,
          onButtonClick: doVerifyAge,
        },
      ],
    }"
  />
  <NamiNotificationsPresets ref="notifications" />
</template>

<script setup lang="ts">
import { IconCircle, IconCircleCheck } from "@tabler/icons-vue";
import {
  User,
  type ColorTheme,
  type ImageQuality,
  SettingsSchema,
  type AvailableLanguages,
} from "~/src/api";
import NamiNotificationsPresets, {
  type PresetOptions,
} from "~/components/nami/notifications/NamiNotificationsPresets.vue";
import { getLanguages } from "~/utils/static";

if (process.server)
  throw new Error(
    "Settings components must be rendered on the client. Wrap the component with <client-only></client-only>",
  );

defineProps<{
  isUpdating?: boolean;
}>();
const emit = defineEmits<{
  (e: "update"): void;
}>();

const notifications = ref<InstanceType<typeof NamiNotificationsPresets>>();
const imageQualities: ["low", "medium", "high"] = ["low", "medium", "high"];

const notify = (options: PresetOptions) => {
  if (!notifications.value)
    throw new Error("Notifications component not initialized.");
  return notifications.value.notify(options);
};

const nuxtApp = useNuxtApp();
const authStore = useAuthStore();
const translate = nuxtApp.$i18n.global.t;
const settingsStore = nuxtApp.$settings();

const isPendingAction = ref(false);
const showConfirmAgeModal = ref(false);

const matureEnabled = computed(
  () => settingsStore?.settings.platform.matureContentEnabled,
);
function updateMature(enableMature: boolean) {
  if (!settingsStore) {
    return;
  }

  settingsStore.settings.platform.matureContentEnabled = enableMature;
  showConfirmAgeModal.value = false;
}

type TranslationLocales =
  typeof SettingsSchema.schema.platform.schema.preferredTranslationLocales.TYPE;

const getAllTranslationLocales = async () => {
  const languages = await fetch("/static/chapter/languages.json").then(
    (r) =>
      r.json() as Promise<{
        supportedLanguages: AvailableLanguages[];
      }>,
  );

  const allLanguages = getLanguages();
  const keys = new Set(languages.supportedLanguages);

  return allLanguages.filter((lang) => keys.has(lang.value));
};

const translationLocales = computed(
  () => settingsStore?.settings.platform.preferredTranslationLocales,
);

function updateTranslationLocales(
  newTranslationLocales: typeof SettingsSchema.schema.platform.schema.preferredTranslationLocales.TYPE,
) {
  if (!settingsStore) {
    return;
  }

  settingsStore.settings.platform.preferredTranslationLocales =
    newTranslationLocales;
}

const userHasVerifiedAge = computed(
  () => authStore?.user?.profile.age_verified as boolean,
);

async function doVerifyAge() {
  const token = await authStore.getToken();

  if (!token) {
    throw new Error("Not logged in.");
  }

  isPendingAction.value = true;

  try {
    await User.verifyAge(true, token);

    // Because the data is on keycloak and managed by oidc, we can't mock data,
    // so we're manually refreshing the user with the new data, and letting oidc
    // automatically update our user on the storage, so that we can stay reactive to the change
    const user = (await authStore._manager?.signinSilent()) || null;
    authStore.user = user;

    notify({
      preset: "success.plain",
      detail: "Your age has been verified.",
      timer: 5000,
    });
  } catch (e) {
    const { title, detail } = resolveError(e);

    notify({
      preset: "error.plain",
      title,
      detail,
      timer: 5000,
    });
  }

  isPendingAction.value = false;
  showConfirmAgeModal.value = false;
}
</script>
